import { useEffect, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import pageSuggestionCopy from '@/i18n/ns/page-suggestion-card';
import { documentDiffCopy } from '@/workspace/document-diff-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import type { ActionView } from './action-confirmation';
import type { ActionCopy } from './action-copy';
import { ActionDraftDiff } from './action-draft-diff';
import {
  pageSuggestionView,
  resolveSuggestionTitles,
  type PageSuggestionView,
  type SuggestionTitles,
} from './page-suggestion-view';
import './document-diff.css';

function useSuggestionTitles(action: ActionView): SuggestionTitles | null {
  const active = action.tool === 'suggest_page_edits';
  const key = active
    ? JSON.stringify([action.args.action, action.args.pageId ?? null, action.args.parentId ?? null])
    : '';
  const [resolved, setResolved] = useState<{ key: string; titles: SuggestionTitles } | null>(null);
  useEffect(() => {
    if (!active) return;
    let current = true;
    void resolveSuggestionTitles(action.args).then((titles) => {
      if (current) setResolved({ key, titles });
    });
    return () => {
      current = false;
    };
  }, [key]);
  return resolved?.key === key ? resolved.titles : null;
}

/** The readable view of a suggest_page_edits card; null for every other tool. */
export function usePageSuggestion(action: ActionView): PageSuggestionView | null {
  const copy = useNamespaceCopy(pageSuggestionCopy);
  const titles = useSuggestionTitles(action);
  return action.tool === 'suggest_page_edits'
    ? pageSuggestionView(action.args, titles, copy)
    : null;
}

/** Changes: a new title in words, then the text diff when the text changes. */
export function PageSuggestionChanges({
  view,
  action,
  copy,
  onStats,
}: {
  view: PageSuggestionView;
  action: ActionView;
  copy: ActionCopy;
  onStats?: (stats: { addedWords: number; removedWords: number } | null) => void;
}) {
  const diffCopy = documentDiffCopy(useChatInterfaceLocale());
  useEffect(() => {
    if (!view.textChange) onStats?.(null);
  }, [view.textChange, onStats]);
  return (
    <>
      {view.rename && <p className="pc-action-suggestion-rename">{view.rename}</p>}
      {view.textChange ? (
        <ActionDraftDiff action={action} copy={copy} onStats={onStats} />
      ) : (
        !view.rename && <p className="pc-action-diff-status">{diffCopy.noChanges}</p>
      )}
    </>
  );
}
